import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import Loading from '@components/core/loading';
import GenerateReportDialog from '@components/shared/generate-report-dialog';
import { GENERATED_REPORTS_GRID_COLUMNS } from '@components/shared/manage-reports-templates/generated-reports-grid/generated-reports-grid.data';
import {
    getGeneratedReportsGridData,
    mapReportColumns,
} from '@components/shared/manage-reports-templates/generated-reports-grid/generated-reports-grid.helper';
import {
    GeneratedReportsGridProps,
    REPORTS_GRID_VIEWS,
} from '@components/shared/manage-reports-templates/generated-reports-grid/generated-reports-grid.interface';
import { GeneratedReport, GeneratedReportStatus } from '@interfaces/report-templates.interface';
import { ISwitchMenu } from '@interfaces/svt.interface';
import { ReportStatusUpdatedFields } from '@interfaces-clean/reports.interface';
import { AuditServiceType } from 'types/graphql-global-types';
import EditGeneratedReport from 'views/components/shared/edit-generated-report';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants, { MODAL_CLOSE_ACTIONS, ModuleNames } from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { downloadBase64File } from '@utils/file-service';
import NotificationService from '@utils/notification';
import { addPrefixedRecordIdsToTemplates } from '@utils/prefixes-helper';
import { connectToReportingServer, reportingConnection } from '@utils/reporting-signalr-service';
import { isReportStatusUpdated } from '@utils/shared';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { GET_DATASETS } from '@queries/central-monitoring';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { dataSets, dataSetsVariables } from '@queries/generated/central-monitoring.generated';
import { DataSetFields } from '@queries/generated/fragments.generated';
import { useGetGeneratedReports, useMutationDownloadReport } from '@apis/bff-api/report-templates';

import './styles.scss';

const GeneratedReportsGrid = ({ orgKey, module, currentTemplate, isGenerationHistory }: GeneratedReportsGridProps) => {
    const { t } = useTranslation();
    const localState = useLocalState();
    const user = useAuthorization();

    const [allGeneratedReports, setAllGeneratedReports] = useState<GeneratedReport[]>([]);
    const [historyGeneratedReports, setHistoryGeneratedReports] = useState<GeneratedReport[]>([]);
    const [gridView, setGridView] = useState<REPORTS_GRID_VIEWS>(REPORTS_GRID_VIEWS.myReports);
    const [currentReport, setCurrentReport] = useState<GeneratedReport | null>(null);

    const showEditReport = useBoolean(false);
    const showNewGenerateReport = useBoolean(false);

    const studyId = localState.state?.selectedStudy?.id;
    const studyName = localState.state?.selectedStudy?.studyNumber;
    const gridTitle = isGenerationHistory
        ? t(gridView === REPORTS_GRID_VIEWS.myReports ? 'cm:myReports' : 'cm:allReports')
        : t('cm:generatedReports');

    const {
        data: generatedReportsData,
        mutate,
        isLoading,
        isValidating,
    } = useGetGeneratedReports(orgKey, studyId, module, currentTemplate?.id);
    const { data: dataSetsData, loading } = useQuery<dataSets, dataSetsVariables>(GET_DATASETS, {
        variables: { studyId },
    });
    const { trigger: downloadGeneratedReport, isMutating } = useMutationDownloadReport(
        currentReport?.id,
        studyId,
        module,
    );
    const location = useLocation();
    const isCM = module === Constants.Modules.CM || location.pathname.includes('central-monitoring');
    const canEdit = !!user?.canWrite?.({
        module,
        element: isCM ? Constants.DataElements.CentralMonitoring : Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const canRead = !!user?.canRead?.({
        module,
        element: isCM ? Constants.DataElements.CentralMonitoring : Constants.DataElements.RiskAssessment,
        permission: Permissions.Read,
    });
    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );

    const moduleName = isCM ? ModuleNames.CM : ModuleNames.RAM;

    const exportFileName = currentTemplate?.templateName
        ? `${studyName}_${currentTemplate?.templateName}_${moduleName} Generated Reports List_${stringDateWithoutTimeFromCurrentUtcDate(
              'DD-MMM-YYYY',
          )}`
        : `${studyName}_${moduleName} Generated Reports List_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY')}`;

    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
        mapColumns: mapReportColumns,
    };

    useEffect(() => {
        if (isGenerationHistory || currentTemplate) {
            setAllGeneratedReports(
                orderBy(
                    getGeneratedReportsGridData(
                        generatedReportsData,
                        dataSetsData?.dataSets as DataSetFields[],
                        prefixesData?.auditRecordIdPrefixes,
                    ),
                    [{ field: 'createdDate', dir: 'desc' }],
                ),
            );
        }
    }, [generatedReportsData, dataSetsData]);

    useEffect(() => {
        if (!isGenerationHistory) return;
        const reports =
            gridView === REPORTS_GRID_VIEWS.myReports
                ? allGeneratedReports.filter((item) => item.userId === user?.id)
                : allGeneratedReports;
        const reportsWithPrefixedIRecordIds = addPrefixedRecordIdsToTemplates(
            reports,
            AuditServiceType.REPORTING,
            prefixesData?.auditRecordIdPrefixes,
        );
        setHistoryGeneratedReports(reportsWithPrefixedIRecordIds);
    }, [gridView, allGeneratedReports]);

    useEffect(() => {
        connectToReportingServer(user);
    }, []);

    const reportMessageForUserHandler = (message: string) => {
        NotificationService.showNotification(t(`reports:${message}`));
    };

    const generationUpdateForUserHandler = (data: ReportStatusUpdatedFields) => {
        if (data && isReportStatusUpdated(allGeneratedReports, data)) {
            mutate().catch((e) => window.console.error(e));
        }
    };

    useEffect(() => {
        reportingConnection.on('ReportMessageForUser', reportMessageForUserHandler);
        reportingConnection.on('GenerationUpdateForUser', generationUpdateForUserHandler);

        return () => {
            reportingConnection.off('ReportMessageForUser', reportMessageForUserHandler);
            reportingConnection.off('GenerationUpdateForUser', generationUpdateForUserHandler);
        };
    }, [allGeneratedReports]);

    const onEditClose = (action?: string) => {
        setCurrentReport(null);
        if (action === MODAL_CLOSE_ACTIONS.SUCCESS) {
            mutate().catch((e) => window.console.error(e));
        }

        showEditReport.setFalse();
    };

    const onGenerationClose = (action?: string) => {
        if (action === MODAL_CLOSE_ACTIONS.SUCCESS) {
            mutate().catch((e) => window.console.error(e));
        }

        showNewGenerateReport.setFalse();
    };

    const onDownloadReport = () => {
        downloadGeneratedReport()
            .then((data) => {
                const fileData = data?.[0];
                if (!fileData) {
                    NotificationService.showNotification(t('reports:reportInNotAvailable'));
                    return;
                }

                downloadBase64File(`${fileData.filename}.${fileData.fileType}`, fileData.contentBase64);
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });
    };

    const getRowActions = (item: GeneratedReport): IActionItemV2[] => [
        ...(!isGenerationHistory
            ? [
                  {
                      label: 'reports:editReport',
                      icon: ['fal', 'edit'],
                      onClick: () => {
                          showEditReport.setTrue();
                          setCurrentReport(item);
                      },
                      enabled:
                          canEdit &&
                          [GeneratedReportStatus.Ready, GeneratedReportStatus.Obsolete].includes(item.status!),
                  } as IActionItemV2,
              ]
            : []),
        {
            label: 'reports:downloadReport',
            icon: ['fal', 'download'],
            onClick: () => {
                setCurrentReport(item);
                onDownloadReport();
            },
            enabled: canRead && [GeneratedReportStatus.Ready, GeneratedReportStatus.Obsolete].includes(item.status!),
        },
    ];

    const extraMenuItems: ExtraMenuItem[] = [
        {
            position: ExtraMenuItemPosition.expandMenu,
            customClassName: 'grid-header__item--always-visible',
            expandMenuItems: [
                {
                    label: 'reports:generateReport',
                    onClick: () => showNewGenerateReport.setTrue(),
                    icon: ['fal', 'add'],
                    enabled: canEdit && !!currentTemplate?.inUse,
                },
            ],
        },
    ];

    const switchMenu: ISwitchMenu[] = [
        {
            label: t('cm:myReports'),
            value: REPORTS_GRID_VIEWS.myReports,
            onClick: (value: string) => {
                setGridView(value as REPORTS_GRID_VIEWS);
            },
            currentValue: gridView,
        },
        {
            label: t('cm:allReports'),
            value: REPORTS_GRID_VIEWS.allReports,
            onClick: (value: string) => {
                setGridView(value as REPORTS_GRID_VIEWS);
            },
            currentValue: gridView,
        },
    ];

    if (loading || isLoading || isValidating || isLoadingPrefixes) return <Loading />;

    return (
        <>
            {isMutating && <Loading float />}
            {showEditReport.value && !!currentReport && !!currentTemplate && (
                <EditGeneratedReport report={currentReport} onClose={onEditClose} />
            )}
            {showNewGenerateReport.value && !!currentTemplate && (
                <GenerateReportDialog
                    orgKey={orgKey}
                    module={module}
                    latestReport={allGeneratedReports[0]}
                    template={currentTemplate}
                    onClose={onGenerationClose}
                />
            )}
            <ExpandableGridV2
                className="generated-reports-grid"
                title={gridTitle}
                customKey="generated-reports-grid"
                id="generatedReportsGrid"
                key="generatedReportsGrid"
                items={isGenerationHistory ? historyGeneratedReports : allGeneratedReports}
                hideCheckbox
                hideExpand
                scrollable="scrollable"
                groupable
                reorderable
                resizable
                pageable={false}
                switchMenu={isGenerationHistory ? switchMenu : undefined}
                extraMenuItems={!isGenerationHistory ? extraMenuItems : undefined}
                rowActions={getRowActions}
                singleRowAction={isGenerationHistory}
                columns={GENERATED_REPORTS_GRID_COLUMNS(
                    t,
                    module,
                    isGenerationHistory,
                    currentTemplate?.includeNameAndVersion,
                    currentTemplate?.includeComparisonDataset,
                )}
                exportConfig={exportConfig}
            />
        </>
    );
};

export default GeneratedReportsGrid;
